import {
  isCeilingHeightShownForRoomTag,
  isFloorMaterialShownForRoomTag,
} from "building-generation/elements/roomTagTools";
import { getFloorElevationAndCeilingHeight } from "building-generation/generationTools/generateCeilingsTools";
import type { FloorAssembly } from "building-types/floors/floorAssemblyTypes";
import type { FloorSystem } from "building-types/floors/floorSystemTypes";
import type { Room } from "building-types/rooms/roomTypes";
import type { Table } from "common/data/table";
import { pathStringFromMaterialSrcWithUserDefinedName } from "common/materials/materialSrcFromPathString";
import { formatInchesFraction } from "doc/measurementUtils";
import { polyLine2Area } from "geom/polyLine2";
import { ROOM_AREA_THRESHOLD_TO_SWITCH_TO_SHORT_NAME } from "./roomLabelTypes";
import type { RoomLabelFormatterSettings } from "./roomLabelTypes";

export function ceilingHeightWithOverride(roomFloorAssemblies: Table<FloorAssembly>, room: Room) {
  if (room.ceilingHeightTagOverride) {
    return room.ceilingHeightTagOverride;
  }
  const roomFloorAssembly = roomFloorAssemblies[room.id];

  if (roomFloorAssembly) {
    const { ceilingHeight } = getFloorElevationAndCeilingHeight(room, roomFloorAssembly);
    return formatInchesFraction(ceilingHeight, 16);
  }
  return "";
}

function ceilingHeightToRoomTag(
  settings: RoomLabelFormatterSettings,
  roomFloorAssemblies: Table<FloorAssembly>,
  room: Room,
) {
  if (!isCeilingHeightShownForRoomTag(room, settings)) {
    return "";
  }
  return ceilingHeightWithOverride(roomFloorAssemblies, room);
}

export function floorMaterialWithOverride(floorSystems: Table<FloorSystem>, room: Room) {
  if (room.floorMaterialTagOverride) {
    return room.floorMaterialTagOverride;
  }
  const floorFinishes = floorSystems[room.level.id]?.roomFloorFinishes[room.id];
  if (floorFinishes !== undefined) {
    const materials = {};
    for (const floorFinish of floorFinishes) {
      materials[pathStringFromMaterialSrcWithUserDefinedName(floorFinish.materialSrc)] = true;
    }
    for (const mat of Object.keys(materials)) {
      return mat;
    }
  }
  return "";
}

function floorMaterialToRoomTag(settings: RoomLabelFormatterSettings, floorSystems: Table<FloorSystem>, room: Room) {
  if (!isFloorMaterialShownForRoomTag(room, settings)) {
    return "";
  }
  return floorMaterialWithOverride(floorSystems, room);
}

function tagDetailsToRoomTag(tagDetails: string | undefined) {
  if (tagDetails) {
    return tagDetails;
  }
  return "";
}

function withNewline(s: string) {
  if (s.length > 0) {
    return "\n" + s;
  }
  return s;
}

// Re-export from shared types for backwards compatibility
export type { RoomLabelFormatterSettings, RoomLabelFormatter } from "./roomLabelTypes";

export function formatRoomLabelDefault(
  settings: RoomLabelFormatterSettings,
  room: Room,
  roomFloorAssemblies: Table<FloorAssembly>,
  floorSystems: Table<FloorSystem>,
) {
  const { nameInPlan, attributes, outline, tagDetails } = room;

  let label: string;
  if (!outline || polyLine2Area(outline) < ROOM_AREA_THRESHOLD_TO_SWITCH_TO_SHORT_NAME) {
    label = nameInPlan ? nameInPlan.slice(0, 4) + "." : attributes.shortName;
  } else {
    label = nameInPlan || attributes.name;
  }

  label = label.replace(" ", "\n"); // this makes a new line for every space..

  label += withNewline(ceilingHeightToRoomTag(settings, roomFloorAssemblies, room));

  label += withNewline(floorMaterialToRoomTag(settings, floorSystems, room));

  label += withNewline(tagDetailsToRoomTag(tagDetails));
  return label;
}

function nameOrfallback(name: string | undefined, fallback: string): string {
  if (name) {
    const trimmed = name.trim();
    if (trimmed.length > 0) {
      return trimmed;
    }
  }
  return fallback.trim();
}

export function formatRoomLabelSanitized(
  settings: RoomLabelFormatterSettings,
  room: Room,
  roomFloorAssemblies: Table<FloorAssembly>,
  floorSystems: Table<FloorSystem>,
): string {
  const { nameInPlan, attributes, tagDetails } = room;

  // `nameInPlan` falls back to `name` if an empty string
  let formattedName = nameOrfallback(nameInPlan, attributes.name);

  if (!room?.outline || polyLine2Area(room?.outline) < ROOM_AREA_THRESHOLD_TO_SWITCH_TO_SHORT_NAME) {
    // `nameInPlan trimmed` falls back to `shortName` if an empty string
    const abbreviated = nameInPlan ? nameInPlan.trim().slice(0, 4) + "." : "";
    formattedName = nameOrfallback(abbreviated, attributes.shortName);
  }

  formattedName += withNewline(ceilingHeightToRoomTag(settings, roomFloorAssemblies, room));

  formattedName += withNewline(floorMaterialToRoomTag(settings, floorSystems, room));

  formattedName += withNewline(tagDetailsToRoomTag(tagDetails));

  return formattedName;
}
